import {
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewEncapsulation,
    signal,
    input,
    effect,
    viewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { Table } from "../../s25-table/Table";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { GenericTableFadePreviewComponent } from "../../s25-table/generics/generic.table.fade.preview.component";
import { GenericTableRenderHTMLComponent } from "../../s25-table/generics/generic.table.render.html.component";
import { Workflow } from "../service/Workflow";
import { WorkflowService } from "../service/workflow.service";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { StateService } from "../../../services/state.service";
import { S25States } from "../../../services/state/state.map";

@TypeManagerDecorator("workflow-action-history-page")
@Component({
    selector: "workflow-action-history-page",
    template: `
        @if (init()) {
            <div class="page-header">
                <h1>Workflow Action History</h1>
                <div class="page-controls">
                    <s25-ng-checkbox [(value)]="showErrorsOnly" (valueChange)="refreshTable()"
                        >Show Errors Only</s25-ng-checkbox
                    >
                    <label class="filter-control">
                        <span>From:</span>
                        <s25-datepicker [(value)]="fromDate" />
                    </label>
                    <label class="filter-control">
                        <span>To:</span>
                        <s25-datepicker [(value)]="toDate" />
                    </label>
                    <button class="aw-button aw-button--primary" (click)="refreshTable()">Search</button>
                </div>
            </div>

            @if (errorMessage()) {
                <div class="error-alert">
                    <div class="error-icon">⚠️</div>
                    <div class="error-text">{{ errorMessage() }}</div>
                </div>
            }

            <s25-ng-table
                [caption]="'Workflow Action History'"
                [unlimitedWidth]="true"
                [columnSortable]="true"
                [dataSource]="tableConfig"
                [hasTotalRowCount]="true"
                [hasRefresh]="true"
                [showHeaderWhenNoData]="true"
            ></s25-ng-table>
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        .page-header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
        }

        .page-controls {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            align-items: center;
        }

        .filter-control {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .error-alert {
            display: flex;
            align-items: center;
            background-color: #fee;
            border: 1px solid #fcc;
            border-radius: 4px;
            padding: 12px;
            margin-bottom: 20px;
        }

        .error-icon {
            margin-right: 8px;
        }

        .error-text {
            flex: 1;
        }
    `,
    standalone: true,
    imports: [FormsModule, S25TableModule, S25DatepickerModule, S25LoadingInlineModule, S25CheckboxModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.Emulated,
})
export class WorkflowActionHistoryPageComponent implements OnInit {
    init = signal(false);
    history = signal<Workflow.WorkflowActionHistory[]>([]);
    showErrorsOnly = signal(false);
    dateFormat: string = "";
    errorMessage = signal<string>("");

    historyTable = viewChild<S25TableComponent>(S25TableComponent);

    // Date models for date pickers
    fromDate = signal<Date | null>(null);
    toDate = signal<Date | null>(null);

    tableConfig: Table.DataSource;

    // Auto-refresh when component becomes visible
    activeMode = input<string | null>(null);

    constructor(private cd: ChangeDetectorRef) {
        // Auto-refresh when component becomes visible (transitions from hidden to visible)
        let wasVisible = false;
        let hasInitialized = false;
        effect(() => {
            const isVisible = this.activeMode() === "workflowActionHistory";
            if (isVisible && !wasVisible && hasInitialized) {
                // Component just became visible after being hidden - refresh
                this.refreshTable();
            }
            if (isVisible) {
                hasInitialized = true;
            }
            wasVisible = isVisible;
        });
    }

    async ngOnInit() {
        this.dateFormat = await UserprefService.getS25DateTimeformat();

        // Set default date range to last 30 days
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);

        this.fromDate.set(thirtyDaysAgo);
        this.toDate.set(today);

        this.initializeTableConfig();

        this.init.set(true);
    }

    private initializeTableConfig() {
        const columns: Table.Column[] = [
            { id: "workflowItem", header: "Workflow Item" },
            { id: "workflowItemName", header: "Item Name" },
            { id: "objectId", header: "Object" },
            { id: "actionType", header: "Action Type" },
            { id: "actionCode", header: "Action Code" },
            { id: "status", header: "Status" },
            { id: "executionDate", header: "Execution Date" },
            { id: "errorMessage", header: "Error Message" },
            { id: "httpResponseCode", header: "Response Code" },
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getHistoryData,
        };
    }

    getHistoryData = async (): Promise<Table.DataSourceResponse> => {
        await this.loadHistory();

        // Filter by errors only if checkbox is checked
        let filteredHistory = this.history();
        if (this.showErrorsOnly()) {
            filteredHistory = filteredHistory.filter((item) => item.status === "ERROR");
        }

        // Sort by executionDate descending (newest first)
        filteredHistory.sort((a, b) => {
            const dateA = a.executionDate ? new Date(a.executionDate).getTime() : 0;
            const dateB = b.executionDate ? new Date(b.executionDate).getTime() : 0;
            return dateB - dateA; // Descending order
        });

        return {
            rows: filteredHistory.map((item) => ({
                id: item.workflowActionHistoryId?.toString() || "",
                name: `${item.workflowItemName} - ${item.actionCode} (${item.actionType})`,
                cells: {
                    executionDate: {
                        text: this.formatDate(item.executionDate),
                        sortValue: item.executionDate || "",
                    },
                    status: { text: item.status || "" },
                    actionType: { text: item.actionType },
                    actionCode: { text: item.actionCode || "" },
                    workflowItem: { text: `${item.workflowItemTypeId}:${item.workflowItemId}` },
                    workflowItemName: { text: item.workflowItemName || "" },
                    objectId: this.buildObjectIdCell(item.httpResponseBody),
                    errorMessage: {
                        component: GenericTableFadePreviewComponent,
                        inputs: {
                            cell: {
                                text: item.errorMessage || "",
                            },
                        },
                    },
                    httpResponseCode: { text: item.httpResponseCode?.toString() || "N/A" },
                },
                data: item,
            })),
        };
    };

    async loadHistory() {
        const from = this.fromDate();
        const to = this.toDate();

        if (!from || !to) {
            this.errorMessage.set("Please select a date range");
            return;
        }

        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);

        // Validate that start date is before end date
        if (from > to) {
            this.errorMessage.set("Start date must be before end date");
            return;
        }

        this.errorMessage.set(""); // Clear any previous errors
        try {
            const history = await WorkflowService.getActionHistory(from, to);
            this.history.set(history || []);
        } catch (error) {
            console.error("Failed to load workflow action history:", error);
            this.errorMessage.set("Failed to load workflow action history. Please try again.");
            this.history.set([]);
        }
    }

    refreshTable() {
        this.historyTable()?.refresh(true, true);
    }

    private buildObjectIdCell(httpResponseBody?: string): Table.Cell {
        if (!httpResponseBody) return { text: "" };
        try {
            const parsed = JSON.parse(httpResponseBody);
            if (parsed?.objectId && parsed?.objectType) {
                const objectId = parsed.objectId;
                const objectType = parsed.objectType;
                let href: string | undefined;
                if (objectType === "EVENT") {
                    href = StateService.href(S25States.names.studentEngagementEventDetails, { itemId: objectId });
                } else if (objectType === "ORGANIZATION") {
                    href = StateService.href(S25States.names.seOrganizationDetails, { itemId: objectId });
                }
                if (href) {
                    return {
                        component: GenericTableRenderHTMLComponent,
                        inputs: { text: `<a href="${href}">${objectType} ${objectId}</a>`, isHtml: true },
                    };
                }
                return { text: `${objectType} ${objectId}` };
            }
        } catch {
            // Not valid JSON or not the expected format
        }
        return { text: "" };
    }

    private formatDate(dateString?: string) {
        if (!dateString) return "N/A";
        return S25Datefilter.transform(dateString, this.dateFormat) || dateString;
    }
}
